// Subscription Service for fetching subscription plans
const API_BASES = [
  process.env.REACT_APP_API_BASE_URL, // explicit backend in dev
  "https://api.quvikme.com", // production fallback
].filter(Boolean);

// Helper function to make API requests with retry logic across multiple bases
const apiRequestWithRetry = async (endpoint, options = {}) => {
  // Get admin token from session storage
  const adminToken = sessionStorage.getItem("adminToken");

  const defaultOptions = {
    headers: {
      "Content-Type": "application/json",
      ...(adminToken && { Authorization: `Bearer ${adminToken}` }),
    },
  };

  let lastErr = null;

  // Try to fetch from each base URL until one works
  for (const base of API_BASES) {
    try {
      const url = `${base}${endpoint}`;
      const response = await fetch(url, {
        ...defaultOptions,
        ...options,
      });

      if (response.ok) {
        return await response.json();
      } else {
        lastErr = new Error(`Failed to load data from ${base}`);
        continue;
      }
    } catch (error) {
      lastErr = error;
      continue;
    }
  }

  throw lastErr || new Error("Failed to load data from any endpoint");
};

export const subscriptionService = {
  // Fetch user subscription plans
  async getUserPlans() {
    try {
      const data = await apiRequestWithRetry("/api/subscription-plans/users");
      return data.plans || [];
    } catch (error) {
      console.error("Error fetching user plans:", error);
      throw error;
    }
  },

  // Fetch vendor subscription plans
  async getVendorPlans() {
    try {
      const data = await apiRequestWithRetry("/api/subscription-plans/vendors");
      return data.plans || [];
    } catch (error) {
      console.error("Error fetching vendor plans:", error);
      throw error;
    }
  },

  // Create user subscription plan
  async createUserPlan(planData) {
    try {
      const data = await apiRequestWithRetry("/api/subscription-plans/users", {
        method: "POST",
        body: JSON.stringify(planData),
      });
      return data.plan;
    } catch (error) {
      console.error("Error creating user plan:", error);
      throw error;
    }
  },

  // Create vendor subscription plan
  async createVendorPlan(planData) {
    try {
      const data = await apiRequestWithRetry(
        "/api/subscription-plans/vendors",
        {
          method: "POST",
          body: JSON.stringify(planData),
        }
      );
      return data.plan;
    } catch (error) {
      console.error("Error creating vendor plan:", error);
      throw error;
    }
  },

  // Update user subscription plan
  async updateUserPlan(planId, planData) {
    try {
      const data = await apiRequestWithRetry(
        `/api/subscription-plans/users/${planId}`,
        {
          method: "PUT",
          body: JSON.stringify(planData),
        }
      );
      return data.plan;
    } catch (error) {
      console.error("Error updating user plan:", error);
      throw error;
    }
  },

  // Update vendor subscription plan
  async updateVendorPlan(planId, planData) {
    try {
      const data = await apiRequestWithRetry(
        `/api/subscription-plans/vendors/${planId}`,
        {
          method: "PUT",
          body: JSON.stringify(planData),
        }
      );
      return data.plan;
    } catch (error) {
      console.error("Error updating vendor plan:", error);
      throw error;
    }
  },

  // Delete user subscription plan
  async deleteUserPlan(planId) {
    try {
      await apiRequestWithRetry(`/api/subscription-plans/users/${planId}`, {
        method: "DELETE",
      });
      return true;
    } catch (error) {
      console.error("Error deleting user plan:", error);
      throw error;
    }
  },

  // Delete vendor subscription plan
  async deleteVendorPlan(planId) {
    try {
      await apiRequestWithRetry(`/api/subscription-plans/vendors/${planId}`, {
        method: "DELETE",
      });
      return true;
    } catch (error) {
      console.error("Error deleting vendor plan:", error);
      throw error;
    }
  },

  // Get plans grouped by tier
  async getGroupedUserPlans() {
    try {
      const data = await apiRequestWithRetry(
        "/api/subscription-plans/users/grouped"
      );
      return data.plans || {};
    } catch (error) {
      console.error("Error fetching grouped user plans:", error);
      throw error;
    }
  },

  // Get vendor plans grouped by tier
  async getGroupedVendorPlans() {
    try {
      const data = await apiRequestWithRetry(
        "/api/subscription-plans/vendors/grouped"
      );
      return data.plans || {};
    } catch (error) {
      console.error("Error fetching grouped vendor plans:", error);
      throw error;
    }
  },

  // Update tier features (applies to both quarterly and yearly)
  async updateUserTierFeatures(tier, features) {
    try {
      const data = await apiRequestWithRetry(
        `/api/subscription-plans/users/tier/${tier}`,
        {
          method: "PUT",
          body: JSON.stringify({ features }),
        }
      );
      return data.plans;
    } catch (error) {
      console.error("Error updating user tier features:", error);
      throw error;
    }
  },

  // Update vendor tier features (applies to both quarterly and yearly)
  async updateVendorTierFeatures(tier, features) {
    try {
      const data = await apiRequestWithRetry(
        `/api/subscription-plans/vendors/tier/${tier}`,
        {
          method: "PUT",
          body: JSON.stringify({ features }),
        }
      );
      return data.plans;
    } catch (error) {
      console.error("Error updating vendor tier features:", error);
      throw error;
    }
  },

  // Fetch all subscription plans (for admin)
  async getAllPlans() {
    try {
      const data = await apiRequestWithRetry("/api/subscription-plans");
      return data.subscriptionPlans || { userPlans: [], vendorPlans: [] };
    } catch (error) {
      console.error("Error fetching all plans:", error);
      throw error;
    }
  },
};

export default subscriptionService;
